import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
// material
import { Card, Button, Typography, Box } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import ImageUploading from 'react-images-uploading';
// components
import FadeLoader from 'react-spinners/FadeLoader';
import tracker from '../../../api/tracker';
import Iconify from '../../../components/Iconify';
import placeholder from './placeholder-video.png';
// utils
import { compressImage } from '../../../utils/imageCompression';
// ----------------------------------------------------------------------

const override = { margin: '0 auto' };

export default function EventPhotos({ event, onPhotoCountUpdate }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { hashid: paramHashId } = useParams();
  const [photos, setPhotos] = useState([]);
  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState(null);
  const { t } = useTranslation();

  const hashid = event?.hashid || paramHashId;

  const fetchEventData = async () => {
    if (!hashid) {
      setMessage(t('No event ID provided'));
      setMessageType('error');
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const response = await tracker.get(`events/${hashid}`);
      const eventPhotos = (response.data.data.photos || []).map((photo) => ({
        hashid: photo.hashid,
        url: photo.url,
      }));
      setPhotos(eventPhotos);
      if (onPhotoCountUpdate) {
        onPhotoCountUpdate(eventPhotos.length);
      }
    } catch (error) {
      console.error('Error fetching event data:', error);
      setMessage(t('Failed to load photos'));
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async () => {
    const newFiles = photos.filter((photo) => photo.file && !photo.hashid);
    if (newFiles.length === 0) {
      setMessage(t('No new photos or videos to upload'));
      setMessageType('error');
      return;
    }

    const formData = new FormData();
    setSaving(true);

    try {
      const processedFiles = await Promise.all(
        newFiles.map(async (photo) => {
          const file = photo.file;
          if (file.type.startsWith('image/')) {
            return compressImage(file, {
              maxSizeMB: 5,
              maxWidthOrHeight: 1200,
            });
          }
          return file;
        })
      );

      processedFiles.forEach((file) => {
        formData.append('photos[]', file);
      });

      const response = await tracker.post(`events/photos/${hashid}`, formData);
      await fetchEventData();
      setMessage(t('Photos and videos saved successfully'));
      setMessageType('success');
    } catch (error) {
      console.error('Error uploading photos/videos:', error.response?.data || error.message);
      const errorMessage =
        error.response?.data?.message ||
        t('Failed to upload photos or videos you can only upload images or videos of 20MB');
      setMessage(errorMessage);
      setMessageType('error');
    } finally {
      setSaving(false);
    }
  };
  const onChange = (imageList) => {
    setPhotos(imageList);
    if (onPhotoCountUpdate) {
      onPhotoCountUpdate(imageList.filter((photo) => photo.file || photo.hashid).length);
    }
  };

  const onDelete = async (image, index) => {
    if (!image.hashid) {
      const updatedPhotos = photos.filter((_, i) => i !== index);
      setPhotos(updatedPhotos);
      if (onPhotoCountUpdate) {
        onPhotoCountUpdate(updatedPhotos.length);
      }
      setMessage(t('Photo removed successfully'));
      setMessageType('success');
      return;
    }
    setLoading(true);
    try {
      await tracker.delete(`assets/${image.hashid}`);
      await fetchEventData();
      setMessage(t('Photo removed successfully'));
      setMessageType('success');
    } catch (error) {
      console.error('Error deleting photo:', error);
      setMessage(t('Failed to delete photo'));
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const onRemoveAll = async () => {
    setLoading(true);
    try {
      const deletePromises = photos
        .filter((photo) => photo.hashid)
        .map((photo) => tracker.delete(`assets/${photo.hashid}`));
      await Promise.all(deletePromises);
      setPhotos([]);
      if (onPhotoCountUpdate) {
        onPhotoCountUpdate(0);
      }
      setMessage(t('All photos removed successfully'));
      setMessageType('success');
    } catch (error) {
      console.error('Error deleting all photos:', error);
      setMessage(t('Failed to delete all photos'));
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEventData();
  }, [hashid]);

  return (
    <Card style={{ padding: '50px' }}>
      {loading ? (
        <FadeLoader color="blue" cssOverride={override} loading={loading} />
      ) : (
        <>
          {message && (
            <Typography color={messageType === 'success' ? 'green' : 'error'} sx={{ mb: 2 }}>
              {message}
            </Typography>
          )}
          <ImageUploading multiple value={photos} onChange={onChange} allowNonImageType dataURLKey="url">
            {({ imageList, onImageUpload, onImageRemove, isDragging, dragProps }) => (
              <div className="upload__image-wrapper">
                {/* Área draggable */}
                <Box
                  sx={{
                    border: isDragging ? '2px dashed green' : '2px dashed gray',
                    borderRadius: '8px',
                    padding: '20px',
                    backgroundColor: isDragging ? '#e0ffe0' : '#f9f9f9',
                    textAlign: 'center',
                    minHeight: '150px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '20px',
                  }}
                  {...dragProps}
                >
                  <Typography variant="body1" color={isDragging ? 'green' : 'textSecondary'}>
                    {isDragging ? t('Drop here!') : t('Click_or_Drag_files_here')}
                  </Typography>
                  <Button variant="contained" onClick={onImageUpload} sx={{ mt: 2 }}>
                    {t('Click_or_Drag_files_here')}
                  </Button>
                </Box>

                <Button
                  style={{ marginBottom: '50px' }}
                  onClick={onRemoveAll}
                  color="error"
                  startIcon={<Iconify icon="quill:folder-trash" />}
                >
                  {t('remove_all')}
                </Button>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                  {imageList.map((image, index) => (
                    <div key={index} className="image-item">
                      {image.url && image.url.endsWith('.mp4') ? (
                        <video width="100" controls>
                          <source src={image.url} type="video/mp4" />
                          <track kind="captions" label="English captions" src="" default />
                        </video>
                      ) : (
                        <img
                          src={image.url || placeholder}
                          alt=""
                          width="100"
                          onError={(e) => {
                            e.target.src = placeholder;
                          }}
                        />
                      )}
                      <div className="image-item__btn-wrapper">
                        <Button
                          startIcon={<Iconify icon="quill:folder-trash" />}
                          color="error"
                          onClick={() => onDelete(image, index)}
                        >
                          {t('remove')}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </ImageUploading>
          <LoadingButton fullWidth size="large" variant="contained" onClick={onSubmit} loading={saving}>
            {t('Save')}
          </LoadingButton>
        </>
      )}
    </Card>
  );
}
